import { ApolloProvider } from '@apollo/client'
import Image from 'next/image'
import type { AppProps } from 'next/app'
import '../styles/globals.css'
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css'
import 'react-datepicker/dist/react-datepicker.css'
import 'react-big-calendar/lib/css/react-big-calendar.css'
import Wrapper from '@layouts/Wrapper'
import { DefaultTheme, ThemeContext, Themes } from '@libs/useTheme'
import { useEffect, useState } from 'react'
import { Theme } from '@libs/useTheme'
import { apolloClient } from '@libs/apollo-client'
import Box from '@components/base/Box'
import Head from 'next/head'
import { ProfileInfo } from '@type/Hooks/useAuth/Auth.type'
import { AuthContext } from '@libs/Auth/useAuth'
import useLocalStorage from '@libs/useLocalStorage'
import LogRocket from 'logrocket'
import { cornersOfRectangle } from '@dnd-kit/core/dist/utilities/algorithms/helpers'
import {
  configureCognito,
  getHostedAuthenticatedUser,
} from '@libs/Auth/cognito'

export type ComponentWithPageLayout = AppProps & {
  Component: AppProps['Component'] & {
    Layout?: React.ComponentType
  }
}

function App(props: ComponentWithPageLayout) {
  const OGTitle =
    props?.pageProps?.metadata?.title ??
    'Forumm: Events for the bold, weird and brilliant'
  const OGDescription =
    props?.pageProps?.metadata?.description ??
    'Effortless ticketing, check-ins & webinars in one place.'
  const OGImage =
    props?.pageProps?.metadata?.image ??
    'https://forumm-images-prod.s3.eu-west-1.amazonaws.com/user-content/manual-upload/f97ae674-f3d9-497e-bad7-db680ac3e832.png'
  const OGUrl = props?.pageProps?.metadata?.url ?? 'https://app.forumm.to'

  // Checks and sets correct meta image type tags. Sets default to jpg
  const OGImageMatch = OGImage.match(/\S(jpg|jpeg|png|gif|bmp|webp|svg)$/i)
  const OGImageType = 'image/' + OGImageMatch ? OGImageMatch : 'jpg'

  return (
    <>
      <Head>
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, maximum-scale=1"
        />
        <meta property="og:title" content={OGTitle} />
        <meta property="og:description" content={OGDescription} />
        <meta property="og:url" content={OGUrl} />
        <meta property="og:image" content={OGImage} />
        <meta property="og:image:alt" content={`${OGTitle} banner image`} />
        <meta property="og:image:type" content={OGImageType} />
        <meta property="og:type" content="website" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={OGTitle} />
        <meta name="twitter:description" content={OGDescription} />
        <meta name="twitter:image" content={OGImage} />

        <meta name="linkedin:card" content="summary_large_image" />
        <meta name="linkedin:title" content={OGTitle} />
        <meta name="linkedin:description" content={OGDescription} />
        <meta name="linkedin:image" content={OGImage} />

        <link
          rel="icon"
          type="image/svg+xml"
          href="https://app.forumm.to/images/ForummLogo.svg"
        />
        <title>{OGTitle}</title>
      </Head>
      <Main
        Component={props.Component}
        pageProps={props.pageProps}
        router={props.router}
        __N_SSG={props.__N_SSG}
        __N_SSP={props.__N_SSP}
      />
    </>
  )
}

function Main({ Component, pageProps, router }: ComponentWithPageLayout) {
  const Layout = Component.Layout || Wrapper

  const [savedTheme, setSavedTheme] = useLocalStorage(
    'Forumm_theme',
    DefaultTheme.type!
  )
  const [redirectTarget, setRedirectTarget] = useLocalStorage<
    | {
        path?: string
      }
    | undefined
  >('Forumm_redirect_target', undefined)

  const [theme, setTheme] = useState<Theme>(
    Themes.find((t) => t.type === savedTheme) ?? DefaultTheme
  )
  const [profile, setProfile] = useState<ProfileInfo | undefined>()
  const [syncProfile, setSyncProfile] = useState<() => void>(() => () => {})
  const [isMounted, setIsMounted] = useState<boolean>(false)

  useEffect(() => {
    configureCognito()
    setIsMounted(true)
    if (
      typeof window !== 'undefined' &&
      window.location.hostname === 'app.forumm.to'
    ) {
      LogRocket.init('y4tema/forumm')

      const browserInfo = {
        userAgent: navigator.userAgent,
        platform: navigator.platform,
        language: navigator.language,
        appVersion: navigator.appVersion,
        vendor: navigator.vendor,
        cookieEnabled: navigator.cookieEnabled,
        hardwareConcurrency: navigator.hardwareConcurrency,
        online: navigator.onLine,
        maxTouchPoints: navigator.maxTouchPoints,
      }

      console.log('Browser Info: ', browserInfo)
      LogRocket.track('Browser Info LogRocket', browserInfo)
    }
  }, [])

  useEffect(() => {
    if (profile?.email) {
      LogRocket.identify(profile.email, {
        name: profile.email,
        email: profile.email,
      })
    }
  }, [profile])

  useEffect(() => {
    console.log({ redirectTarget })
    if (!redirectTarget?.path || router.asPath === redirectTarget.path) return

    getHostedAuthenticatedUser()
      .then(() => {
        const path = redirectTarget.path!
        setRedirectTarget(undefined)
        router.replace(path)
      })
      .catch(() => {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [redirectTarget?.path, router.asPath])

  if (!isMounted) return null

  return (
    <>
      <AuthContext.Provider
        value={{ profile, setProfile, syncProfile, setSyncProfile }}
      >
        <ThemeContext.Provider value={{ theme, setTheme }}>
          <ApolloProvider client={apolloClient}>
            {/* @ts-ignore */}
            <Layout>
              {/* @ts-ignore */}
              <Component {...pageProps} />
            </Layout>
            <Box id="modal-root" />
          </ApolloProvider>
        </ThemeContext.Provider>
      </AuthContext.Provider>
    </>
  )
}

export default App
